import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { generateUUID } from "../../common/helpers/generateUUID";
import { buildMappingKey } from "../../common/helpers/costArrResolver";
import { fetchMarkupSettings, saveMarkupSettings, sendMarkupSettingsOnUnload } from "../../api/markupSettingsApi";
import { createRemoteSaveQueue } from "../../common/composables/useRemoteSaveQueue";
import {
  createEmptyMarkupSnapshot,
  hasMarkupData,
  normalizeMarkupSnapshot,
} from "../../common/helpers/eplex/markupSettingsSnapshot";
import { clearLocalMarkupStorage, readLocalMarkupSnapshot } from "../../common/helpers/eplex/markupStorageHelper";
import {
  MARKUP_SETTINGS_VERSION,
  type MarginRule,
  type MarkupSettingsSnapshot,
  type PriceType,
  type Rounding,
} from "../../models/markupSettings";
import i18n from "../../../i18n";

export type { MarginRule, PriceType, Rounding } from "../../models/markupSettings";

/**
 * `idle` — ещё не читали; `loading` — читаем с сервера; `ready` — показываем
 * серверные данные; `error` — сервер не ответил, показываем что было в браузере.
 */
export type MarkupLoadStatus = "idle" | "loading" | "ready" | "error";

const uid = () => generateUUID();

/**
 * Наценки живут на сервере (CAB-778): одна запись на пользователя, общая для всех
 * его браузеров и устройств. Стор читает её при входе, держит в памяти и после
 * каждой правки отправляет целиком через очередь с паузой — так пачка правок в
 * таблице уходит одним запросом, а сбой сети не теряет данные.
 */
export const useEplexMarkupSettingsStore = defineStore("eplexMarkupSettings", () => {
  const status = ref<MarkupLoadStatus>("idle");
  /** Растёт при каждой замене состояния целиком — экраны по нему перестраивают таблицы. */
  const revision = ref(0);

  const rounding = ref<Rounding>("none");
  /** Активная строка таблицы настроек — состояние одного экрана, на сервер не уходит. */
  const activeTypeId = ref<string | null>(null);
  const priceTypes = ref<PriceType[]>([]);
  const rules = ref<MarginRule[]>([]);
  const costTypeMappings = ref<Record<string, string[]>>({});
  const primaryPriceTypeId = ref<Record<string, string | null>>({});
  const selectedPriceTypeIdsByContext = ref<Record<string, string[]>>({});

  const saveQueue = createRemoteSaveQueue<MarkupSettingsSnapshot>({
    save: saveMarkupSettings,
    onError: (error) => console.error("Markup settings save failed: ", error),
  });
  const saveState = saveQueue.state;

  let inflight: Promise<void> | null = null;
  let isApplying = false;
  /** Что сервер знает последним — чтобы не отправлять состояние, которое он уже видел. */
  let lastPersistedKey = "";

  function snapshotBody(): Omit<MarkupSettingsSnapshot, "updatedAt"> {
    return {
      version: MARKUP_SETTINGS_VERSION,
      rounding: rounding.value,
      priceTypes: priceTypes.value,
      rules: rules.value,
      costTypeMappings: costTypeMappings.value,
      primaryPriceTypeId: primaryPriceTypeId.value,
      selectedPriceTypeIds: selectedPriceTypeIdsByContext.value,
    };
  }

  function buildSnapshot(): MarkupSettingsSnapshot {
    return { ...snapshotBody(), updatedAt: new Date().toISOString() };
  }

  function applySnapshot(snapshot: MarkupSettingsSnapshot): void {
    isApplying = true;
    rounding.value = snapshot.rounding;
    priceTypes.value = snapshot.priceTypes;
    rules.value = snapshot.rules;
    costTypeMappings.value = snapshot.costTypeMappings;
    primaryPriceTypeId.value = snapshot.primaryPriceTypeId;
    selectedPriceTypeIdsByContext.value = snapshot.selectedPriceTypeIds;
    isApplying = false;

    const activeStillExists = snapshot.priceTypes.some((type) => type.id === activeTypeId.value);
    if (!activeStillExists) activeTypeId.value = snapshot.priceTypes[0]?.id ?? null;

    lastPersistedKey = JSON.stringify(snapshotBody());
    revision.value += 1;
  }

  // Синхронно, а не после тика: флаг isApplying виден только в момент присваивания
  watch(
    [rounding, priceTypes, rules, costTypeMappings, primaryPriceTypeId, selectedPriceTypeIdsByContext],
    () => {
      if (isApplying || status.value === "loading" || status.value === "idle") return;

      const key = JSON.stringify(snapshotBody());
      if (key === lastPersistedKey) return;

      lastPersistedKey = key;
      saveQueue.schedule(buildSnapshot());
    },
    { deep: true, flush: "sync" },
  );

  /**
   * Первый вход после переезда: на сервере записи нет, а в браузере наценки есть —
   * отдаём их на сервер и стираем локальную копию. Не удалось отправить — копия
   * остаётся до следующего входа, человек работает с ней как раньше.
   */
  async function adoptLocalSnapshot(): Promise<void> {
    const local = readLocalMarkupSnapshot();

    if (!local || !hasMarkupData(local)) {
      applySnapshot(createEmptyMarkupSnapshot());
      clearLocalMarkupStorage();
      return;
    }

    applySnapshot(local);
    try {
      await saveMarkupSettings(buildSnapshot());
      clearLocalMarkupStorage();
    } catch (error) {
      console.error("Markup settings migration failed: ", error);
    }
  }

  async function performLoad(): Promise<void> {
    status.value = "loading";
    try {
      const remote = await fetchMarkupSettings();

      if (remote.kind === "found") {
        // Сервер главнее браузера: на второе устройство приезжает то, что уже сохранено
        applySnapshot(normalizeMarkupSnapshot(remote.raw) ?? createEmptyMarkupSnapshot());
        clearLocalMarkupStorage();
      } else {
        await adoptLocalSnapshot();
      }

      status.value = "ready";
    } catch (error) {
      console.error("Markup settings load failed: ", error);
      // Локальную копию не стираем: сервер не ответил, а не сказал «пусто»
      applySnapshot(readLocalMarkupSnapshot() ?? createEmptyMarkupSnapshot());
      status.value = "error";
    }
  }

  /** Читает наценки с сервера один раз; после сбоя следующий вызов пробует снова. */
  async function load(force = false): Promise<void> {
    if (!force && (status.value === "ready" || status.value === "loading")) {
      await (inflight ?? Promise.resolve());
      return;
    }

    inflight ??= performLoad().finally(() => {
      inflight = null;
    });
    await inflight;
  }

  /** Смена личности под открытой вкладкой (CAB-734): читаем наценки нового человека. */
  async function reload(): Promise<void> {
    await saveQueue.flush();
    await load(true);
  }

  /** Досылает несохранённое при закрытии вкладки. */
  function flushOnUnload(): void {
    const pending = saveQueue.pendingPayload();
    if (pending) sendMarkupSettingsOnUnload(pending);
  }

  const roundValue = (v: number): number => {
    const r = rounding.value;
    if (r === "none") return v;
    const step = Number(r);
    return Math.round(v / step) * step;
  };

  const getTypeById = (id: string | null) => priceTypes.value.find((t) => t.id === id) ?? null;

  const setActiveType = (id: string | null) => {
    activeTypeId.value = id;
  };

  const addPriceType = () => {
    const id = uid();
    priceTypes.value.push({ id, name: i18n.global.t("markupSettings.newPriceType") });

    const newRuleId = uid();
    rules.value.push({
      id: newRuleId,
      typeId: id,
      pluginId: 0,
      threshold: 0,
      percent: 0,
      enabled: true,
    });

    setEnabledRuleIds([...enabledRuleIds.value, newRuleId]);

    activeTypeId.value ??= id;
    return id;
  };

  const addRule = (typeId: string) => {
    rules.value.push({
      id: uid(),
      typeId,
      pluginId: 0,
      threshold: 0,
      percent: 0,
      enabled: true,
    });
  };

  const updateTypeName = (typeId: string, name: string) => {
    const t = priceTypes.value.find((t) => t.id === typeId);
    if (t) t.name = name || i18n.global.t("markupSettings.untitled");
  };

  const updateRulePartial = (ruleId: string, patch: Partial<MarginRule>) => {
    const r = rules.value.find((r) => r.id === ruleId);
    if (r) Object.assign(r, patch);
  };

  const deleteRule = (ruleId: string) => {
    const idx = rules.value.findIndex((r) => r.id === ruleId);
    if (idx >= 0) rules.value.splice(idx, 1);
  };

  const deleteType = (typeId: string) => {
    rules.value = rules.value.filter((r) => r.typeId !== typeId);

    const idx = priceTypes.value.findIndex((t) => t.id === typeId);
    if (idx >= 0) priceTypes.value.splice(idx, 1);
    if (activeTypeId.value === typeId) {
      activeTypeId.value = priceTypes.value[0]?.id ?? null;
    }

    const { [typeId]: _removed, ...restMappings } = costTypeMappings.value;
    costTypeMappings.value = restMappings;

    for (const context of Object.keys(primaryPriceTypeId.value)) {
      if (primaryPriceTypeId.value[context] === typeId) {
        primaryPriceTypeId.value[context] = null;
      }
    }

    cleanupStaleSelections();
  };

  const enabledRuleIds = computed(() => rules.value.filter((r) => r.enabled).map((r) => r.id));

  const setEnabledRuleIds = (ids: string[]) => {
    const set = new Set(ids);
    rules.value.forEach((r) => {
      r.enabled = set.has(r.id);
    });
  };

  const getPercentForType = (basePrice: number, pluginId: number, typeId: string): number | null => {
    const rulesForType = rules.value.filter((r) => r.typeId === typeId);

    const pick = (pid: number) => {
      const candidates = rulesForType
        .filter((r) => r.pluginId === pid && basePrice >= (Number(r.threshold) || 0))
        .sort((a, b) => b.threshold - a.threshold);
      return candidates[0] ?? null;
    };

    const byPlugin = pick(Number(pluginId) || 0);
    if (byPlugin) return Number(byPlugin.percent) || 0;

    const byDefault = pick(0);
    return byDefault ? Number(byDefault.percent) || 0 : null;
  };

  const priceWithMarginForType = (basePrice: number, pluginId: number, typeId: string): number | null => {
    const pct = getPercentForType(basePrice, pluginId, typeId);

    if (pct === null) return null;

    const result = basePrice * (1 + pct / 100);
    return roundValue(result);
  };

  function getSelectedPriceTypeIds(context: string): string[] {
    return selectedPriceTypeIdsByContext.value[context] ?? [];
  }

  function setSelectedPriceTypeIds(context: string, ids: string[]) {
    selectedPriceTypeIdsByContext.value = { ...selectedPriceTypeIdsByContext.value, [context]: ids };
  }

  /** Выбор столбцов ссылается только на существующие типы цен. */
  function cleanupStaleSelections() {
    const validIds = new Set(priceTypes.value.map((t) => t.id));
    const current = selectedPriceTypeIdsByContext.value;
    const updated: Record<string, string[]> = {};
    let changed = false;

    for (const context of Object.keys(current)) {
      const filtered = current[context].filter((id) => validIds.has(id));
      updated[context] = filtered;
      if (filtered.length !== current[context].length) changed = true;
    }

    if (changed) selectedPriceTypeIdsByContext.value = updated;
  }

  const setCostTypeMapping = (typeId: string, integrationId: string, costTypeUuid: string | null) => {
    const current = [...(costTypeMappings.value[typeId] ?? [])];
    const prefix = `${integrationId}||`;
    const filtered = current.filter((key) => !key.startsWith(prefix));

    if (costTypeUuid) {
      filtered.push(buildMappingKey(integrationId, costTypeUuid));
    }

    costTypeMappings.value = { ...costTypeMappings.value, [typeId]: filtered };
  };

  const getCostTypeMappingsForType = (typeId: string): string[] => {
    return costTypeMappings.value[typeId] ?? [];
  };

  const enabledPriceTypes = computed(() =>
    priceTypes.value.filter((type) => rules.value.some((r) => r.typeId === type.id && r.enabled)),
  );

  const getPrimaryPriceTypeId = (context: string): string | null => {
    return primaryPriceTypeId.value[context] ?? null;
  };

  const getEffectivePrimaryPriceTypeId = (context: string): string | null => {
    const stored = getPrimaryPriceTypeId(context);
    if (stored && enabledPriceTypes.value.some((t) => t.id === stored)) return stored;
    return enabledPriceTypes.value[0]?.id ?? null;
  };

  const setPrimaryPriceTypeId = (context: string, typeId: string | null) => {
    primaryPriceTypeId.value = { ...primaryPriceTypeId.value, [context]: typeId };
  };

  return {
    status,
    saveState,
    revision,
    rounding,
    activeTypeId,
    priceTypes,
    rules,
    costTypeMappings,
    primaryPriceTypeId,
    load,
    reload,
    flush: saveQueue.flush,
    flushOnUnload,
    setActiveType,
    addPriceType,
    addRule,
    updateTypeName,
    updateRulePartial,
    deleteRule,
    deleteType,
    setEnabledRuleIds,
    enabledRuleIds,
    priceWithMarginForType,
    getPercentForType,
    roundValue,
    getTypeById,
    selectedPriceTypeIdsByContext,
    setSelectedPriceTypeIds,
    getSelectedPriceTypeIds,
    setCostTypeMapping,
    getCostTypeMappingsForType,
    enabledPriceTypes,
    getPrimaryPriceTypeId,
    getEffectivePrimaryPriceTypeId,
    setPrimaryPriceTypeId,
  };
});
